import React, { memo, useEffect, useRef, useCallback } from 'react';

import classNames from 'classnames';

// =============================================================================
// Constants
// =============================================================================

const RADIUS = 45;
const STROKE_WIDTH = 10;
const VIEWBOX_SIZE = 100;
const CENTER = VIEWBOX_SIZE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ROTATION_SPEED = 3; // degrees per frame
const INDETERMINATE_OFFSET_RATIO = 0.75;

// =============================================================================
// Configuration
// =============================================================================

const SIZE_CONFIG = {
    lg: { className: 'tw-w-10 tw-h-10', showPercentage: true, percentageClass: 'tw-text-2xs' },
    md: { className: 'tw-w-8 tw-h-8', showPercentage: true, percentageClass: 'tw-text-3xs' },
    sm: { className: 'tw-w-6 tw-h-6', showPercentage: false },
    xs: { className: 'tw-w-4 tw-h-4', showPercentage: false },
    xxs: { className: 'tw-w-3 tw-h-3', showPercentage: false },
};

const DEFAULT_SIZE = 'md';

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Clamps a value between min and max bounds, handling NaN and invalid values
 * @param {number} value - The value to clamp
 * @param {number} min - Minimum bound
 * @param {number} max - Maximum bound
 * @returns {number} Clamped value, defaults to min if value is NaN or invalid
 */
const clamp = (value, min, max) => {
    const numValue = Number(value);
    if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
        return min;
    }
    return Math.min(max, Math.max(min, numValue));
};

/**
 * Calculates stroke dash offset for a given percentage
 * @param {number} percentage - Progress percentage (0-100)
 * @returns {number} Stroke dash offset value
 */
const calculateStrokeDashOffset = (percentage) => CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Reusable SVG circle for progress visualization
 */
const ProgressCircle = memo(function ProgressCircle({ className, style }) {
    return (
        <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            fill="none"
            className={className}
            style={style}
        />
    );
});

/**
 * Background track circle
 */
const TrackCircle = memo(function TrackCircle() {
    return <circle cx={CENTER} cy={CENTER} r={RADIUS} stroke="currentColor" strokeWidth={STROKE_WIDTH} fill="none" className="tw-text-charcoal-25" />;
});

// =============================================================================
// Custom Hooks
// =============================================================================

/**
 * Hook for managing continuous rotation animation using requestAnimationFrame
 * @param {React.RefObject} elementRef - Ref to the element to animate
 * @returns {void}
 */
const useSpinAnimation = (elementRef) => {
    const animationFrameRef = useRef(null);
    const rotationRef = useRef(0);

    const animate = useCallback(() => {
        rotationRef.current = (rotationRef.current + ROTATION_SPEED) % 360;

        if (elementRef?.current) {
            // Offset by -90 to start from top position
            elementRef.current.style.transform = `rotate(${rotationRef.current - 90}deg)`;
        }

        animationFrameRef.current = requestAnimationFrame(animate);
    }, [elementRef]);

    useEffect(() => {
        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [animate]);
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * CircularProgress - A circular progress indicator component
 *
 * Displays either an indeterminate spinning state (when percentage is 0)
 * or a determinate progress state showing actual progress.
 *
 * @param {Object} props
 * @param {'lg' | 'md' | 'sm' | 'xs' | 'xxs'} [props.size='md'] - Size variant
 * @param {number} [props.percentage=0] - Progress percentage (0-100). 0 shows indeterminate state
 * @param {boolean} [props.showPercentage=true] - Whether to show percentage label (only for lg/md sizes)
 *
 * @example
 * // Indeterminate (spinning) state
 * <CircularProgress percentage={0} />
 *
 * @example
 * // Determinate state with 75% progress
 * <CircularProgress percentage={75} size="lg" />
 */
function CircularProgress({ size = DEFAULT_SIZE, percentage = 0, showPercentage = true }) {
    const spinnerRef = useRef(null);
    const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG[DEFAULT_SIZE];

    // Normalize percentage to valid range
    const normalizedPercentage = clamp(percentage, 0, 100);
    const isIndeterminate = normalizedPercentage === 0;

    // Initialize spin animation (only when indeterminate)
    useSpinAnimation(isIndeterminate ? spinnerRef : null);

    // Pre-calculate values
    const progressOffset = calculateStrokeDashOffset(normalizedPercentage);
    const canShowPercentageLabel = showPercentage && sizeConfig.showPercentage && !isIndeterminate;

    return (
        <div className="tw-flex tw-justify-center tw-items-center">
            <div
                role="progressbar"
                aria-valuenow={isIndeterminate ? undefined : normalizedPercentage}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-label={isIndeterminate ? $t('Loading') : `${$t('Progress')}: ${normalizedPercentage}%`}
                className="tw-relative tw-flex tw-items-center tw-justify-center"
            >
                {/* Indeterminate Spinner */}
                <svg
                    ref={spinnerRef}
                    className={classNames('tw-absolute', sizeConfig.className)}
                    style={{ opacity: isIndeterminate ? 1 : 0 }}
                    viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
                    fill="none"
                    aria-hidden="true"
                >
                    <TrackCircle />
                    <ProgressCircle
                        className="tw-text-blue"
                        style={{
                            strokeDasharray: CIRCUMFERENCE,
                            strokeDashoffset: CIRCUMFERENCE * INDETERMINATE_OFFSET_RATIO,
                        }}
                    />
                </svg>

                {/* Determinate Progress */}
                <svg
                    className={classNames('tw-transform tw--rotate-90', sizeConfig.className)}
                    style={{ opacity: isIndeterminate ? 0 : 1 }}
                    viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
                    fill="none"
                    aria-hidden="true"
                >
                    <TrackCircle />
                    <ProgressCircle
                        className="tw-text-blue tw-transition-all tw-duration-300 tw-ease-in-out"
                        style={{
                            strokeDasharray: CIRCUMFERENCE,
                            strokeDashoffset: progressOffset,
                        }}
                    />
                </svg>

                {/* Percentage Label */}
                {canShowPercentageLabel && (
                    <span className={classNames('tw-absolute tw-font-semibold tw-text-blue', sizeConfig.percentageClass)} aria-hidden="true">
                        {Math.round(normalizedPercentage)}%
                    </span>
                )}
            </div>
        </div>
    );
}

export default memo(CircularProgress);
